import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import Navigation from './Navigation';
import API_BASE_URL from '../config/api';
import { formatDate } from '../utils/date';
import './TripReportPage.css';

const TripReportPage = () => {
  const { slug } = useParams();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchTripReport();
  }, [slug]);

  const fetchTripReport = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/api/trip-reports/${slug}`);
      setReport(response.data);
    } catch (err) {
      console.error('Error fetching trip report:', err);
      setError('Trip report not found');
    } finally {
      setLoading(false);
    }
  };

  // Simple markdown-like rendering (headers and lists)
  const renderContent = (content) => {
    if (!content) return null;
    
    const lines = content.split('\n');
    const elements = [];
    let currentList = [];
    
    const flushList = () => {
      if (currentList.length > 0) {
        elements.push(
          <ul key={`list-${elements.length}`}>
            {currentList.map((item, i) => <li key={i}>{item}</li>)}
          </ul>
        );
        currentList = [];
      }
    };
    
    lines.forEach((line, index) => {
      const trimmed = line.trim();
      
      if (trimmed.startsWith('# ')) {
        flushList();
        elements.push(<h1 key={index}>{trimmed.slice(2)}</h1>);
      } else if (trimmed.startsWith('## ')) {
        flushList();
        elements.push(<h2 key={index}>{trimmed.slice(3)}</h2>);
      } else if (trimmed.startsWith('### ')) {
        flushList();
        elements.push(<h3 key={index}>{trimmed.slice(4)}</h3>);
      } else if (trimmed.startsWith('- ')) {
        currentList.push(trimmed.slice(2));
      } else if (trimmed === '') {
        flushList();
      } else {
        flushList();
        elements.push(<p key={index}>{trimmed}</p>);
      }
    });
    
    flushList();
    return elements;
  };

  if (loading) {
    return (
      <div className="trip-report-page">
        <Navigation />
        <div className="content">
          <div className="loading">Loading...</div>
        </div>
      </div>
    );
  }

  if (error || !report) {
    return (
      <div className="trip-report-page">
        <Navigation />
        <div className="content">
          <div className="error">
            <h2>Trip Report Not Found</h2>
            <p>{error}</p>
            <Link to="/travelogue" className="back-link">← Back to Travelogue</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="trip-report-page">
      <Navigation />
      
      <article className="content">
        <Link to="/travelogue" className="back-link">← Back to Travelogue</Link>
        
        <header className="report-header">
          <div className="meta">
            <span className="category">{report.category}</span>
            {report.visit_date && (
              <span className="date">Visited {formatDate(report.visit_date)}</span>
            )}
          </div>
          <h1>{report.title}</h1>
          <p className="location">
            📍 {report.location_name}
            {report.city && `, ${report.city}`}
            {report.country && `, ${report.country}`}
          </p>
        </header>

        {report.featured_image_url && (
          <div className="featured-image">
            <img src={report.featured_image_url} alt={report.title} />
            {report.featured_image_caption && (
              <p className="caption">{report.featured_image_caption}</p>
            )}
          </div>
        )}

        {report.summary && (
          <div className="summary">
            <p>{report.summary}</p>
          </div>
        )}

        <div className="report-content">
          {renderContent(report.content)}
        </div>

        {report.tags && report.tags.length > 0 && (
          <div className="tags-section">
            <h3>Tags</h3>
            <div className="tags">
              {report.tags.map((tag, index) => (
                <span key={index} className="tag">{tag}</span>
              ))}
            </div>
          </div>
        )}

        {report.gallery_urls && report.gallery_urls.length > 0 && (
          <div className="gallery-section">
            <h3>Gallery</h3>
            <div className="gallery">
              {report.gallery_urls.map((url, index) => (
                <img key={index} src={url} alt={`Gallery image ${index + 1}`} />
              ))}
            </div>
          </div>
        )}
      </article>

      <footer>
        <p>© 2024 Bas Relief Atlas - A Digital Archive of Neo-Assyrian Bas-Reliefs</p>
      </footer>
    </div>
  );
};

export default TripReportPage;
